import {
  Archive,
  BookOpen,
  ClipboardList,
  FileSpreadsheet,
  History,
  PackageCheck,
  PlusCircle,
  Search,
  Store,
  type LucideIcon,
} from "lucide-react";
import type { WholesaleListView } from "../../../shared/domain.js";

/**
 * Presentation vocabulary for the wholesale side (spec 2026-09-17 §4):
 * the lists and pages the wholesale sidebar offers, their names, icons,
 * descriptions and empty states, and the paths under `/wholesale`.
 *
 * As on the service side, everything here is *presentation* of a server
 * decision: which orders belong to a list, the 36-hour window and every
 * status come from `GET /api/wholesale/orders` and are never recomputed.
 */

// ---------------------------------------------------------------------------
// Views (spec §4.2)
// ---------------------------------------------------------------------------

/**
 * The lists the wholesale sidebar shows. `search` is the sidebar's fourth
 * destination (spec §4.2): it lists `GET /api/wholesale/orders/search` hits
 * for the pane's query rather than a server view.
 */
export const WHOLESALE_VIEWS = ["open", "arrived", "history", "all", "search"] as const;

export type WholesaleView = (typeof WHOLESALE_VIEWS)[number];

export const DEFAULT_WHOLESALE_VIEW: WholesaleView = "open";

export function isWholesaleView(value: unknown): value is WholesaleView {
  return typeof value === "string" && (WHOLESALE_VIEWS as readonly string[]).includes(value);
}

/** The server view behind a sidebar list; `search` has none. */
export function serverViewOf(view: WholesaleView): WholesaleListView | null {
  return view === "search" ? null : view;
}

export type WholesaleViewPresentation = Readonly<{
  label: string;
  Icon: LucideIcon;
  description: string;
  emptyTitle: string;
  emptyDescription: string;
}>;

export const WHOLESALE_VIEW_PRESENTATION: Readonly<Record<WholesaleView, WholesaleViewPresentation>> = {
  open: {
    label: "Open",
    Icon: ClipboardList,
    description: "Ordered, partially arrived and needs-attention orders, oldest first.",
    emptyTitle: "Nothing open",
    emptyDescription: "Every wholesale order has arrived. New orders appear here as soon as they are created.",
  },
  arrived: {
    label: "Arrived · 36h",
    Icon: PackageCheck,
    description: "Orders that arrived within the last 36 hours, newest first — ready to pull and hand over.",
    emptyTitle: "No recent arrivals",
    emptyDescription: "Orders show here for 36 hours after every part is verified, then move to History.",
  },
  history: {
    label: "History",
    Icon: History,
    description: "Arrivals older than 36 hours and cancelled orders. Still searchable, never deleted.",
    emptyTitle: "No history yet",
    emptyDescription: "Arrivals older than 36 hours and cancelled orders will be listed here.",
  },
  all: {
    label: "All orders",
    Icon: Archive,
    description: "Every wholesale order, active and historical.",
    emptyTitle: "No wholesale orders yet",
    emptyDescription: "Orders appear here as soon as they are created.",
  },
  search: {
    label: "Search",
    Icon: Search,
    description: "Find an order by WS number, customer, reference, R&R number, part number or description.",
    emptyTitle: "Search wholesale orders",
    emptyDescription: "Type a WS number, customer, reference, R&R number, part number or description.",
  },
};

// ---------------------------------------------------------------------------
// Destinations and paths
// ---------------------------------------------------------------------------

export const WHOLESALE_PATH = "/wholesale";
export const NEW_WHOLESALE_ORDER_PATH = "/wholesale/new";
export const CUSTOMERS_PATH = "/wholesale/customers";
/**
 * The two pages both sides share, under this side's address (spec §4.1:
 * the URL carries the side): the same catalog and report as
 * `/parts/catalog` and `/parts/reports/stock-orders`, framed by the
 * wholesale shell so opening them from here never flips the side.
 */
export const WHOLESALE_CATALOG_PATH = "/wholesale/catalog";
export const WHOLESALE_STOCK_ORDER_REPORT_PATH = "/wholesale/reports/stock-orders";

export type WholesaleDestination =
  | Readonly<{ kind: "view"; view: WholesaleView; label: string; Icon: LucideIcon }>
  | Readonly<{
      kind: "new_order" | "customers" | "stock_order_report" | "catalog";
      label: string;
      Icon: LucideIcon;
      to: string;
    }>;

/** The page destinations that are not lists; each marks its own link current. */
export type WholesalePageDestination = "new_order" | "customers" | "stock_order_report" | "catalog";

/** New order, the four lists, Customers, then the two pages both sides share. */
export const WHOLESALE_DESTINATIONS: readonly WholesaleDestination[] = [
  { kind: "new_order", label: "New wholesale order", Icon: PlusCircle, to: NEW_WHOLESALE_ORDER_PATH },
  ...WHOLESALE_VIEWS.map<WholesaleDestination>((view) => ({
    kind: "view",
    view,
    label: WHOLESALE_VIEW_PRESENTATION[view].label,
    Icon: WHOLESALE_VIEW_PRESENTATION[view].Icon,
  })),
  { kind: "customers", label: "Customers", Icon: Store, to: CUSTOMERS_PATH },
  { kind: "stock_order_report", label: "Stock order report", Icon: FileSpreadsheet, to: WHOLESALE_STOCK_ORDER_REPORT_PATH },
  { kind: "catalog", label: "Parts catalog", Icon: BookOpen, to: WHOLESALE_CATALOG_PATH },
];

/** The wholesale workspace with `orderId` selected on the All orders list. */
export function wholesaleOrderPath(orderId: string): string {
  return `${WHOLESALE_PATH}?view=all&order=${encodeURIComponent(orderId)}`;
}

/** The All orders list narrowed to one customer — their order history (spec §4.6). */
export function customerOrdersPath(customerId: string): string {
  return `${WHOLESALE_PATH}?view=all&customer=${encodeURIComponent(customerId)}`;
}
